import { useState, useMemo, useCallback, useEffect } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import { BalanceCard } from "./wallet/balance-card";
import { QuickStats } from "./wallet/quick-stats";
import { TransactionItem, type Transaction } from "./wallet/transaction-item";
import { formatCurrency, formatDate } from "@/config/locale";
import { t, getTransactionTypeLabel, stripeConnectConfigErrorMessage } from "./wallet/wallet-i18n";
import {
  getProfile,
  getSellerBalance,
  getSellerBalanceTransactions,
  getSellerPayoutSummary,
  getSellerPayoutAccount,
  getSellerAccountStatus,
  getSellerPayoutFailures,
  postSellerConnectOnboarding,
  postSellerConnectUpdate,
  type SellerBalanceTransactionItem,
  type SellerPayoutSummaryResponse,
  type SellerPayoutAccountItem,
  type SellerAccountStatusResponse,
  type SellerPayoutFailureItem,
} from "@/app/api/auth-client";
import { buildStripeWalletReturnUrl } from "@/app/utils/wallet-stripe-urls";

function WalletStripeBusyLabel({ busy, idleLabel }: { busy: boolean; idleLabel: string }) {
  return (
    <span className="inline-flex items-center justify-center gap-2">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden /> : null}
      {busy ? t("opening") : idleLabel}
    </span>
  );
}

interface WalletPageProps {
  onBack: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  onNavigateToWithdraw?: () => void;
}

function balanceTxToTransaction(item: SellerBalanceTransactionItem, defaultTitle: string): Transaction {
  const isCredit = item.net_cents >= 0;
  const amount = Math.abs(item.net_cents) / 100;
  const typeLabel = getTransactionTypeLabel(item.type);
  const title = typeLabel || item.description || item.type || defaultTitle;
  const description = typeLabel || item.type || "";
  return {
    id: item.id,
    type: isCredit ? "received" : "paid",
    title,
    description,
    amount,
    date: new Date(item.created * 1000),
    status: item.status === "available" ? "completed" : "pending",
    availableOn: item.available_on > 0 ? item.available_on : undefined,
  };
}

export function WalletPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onNavigateToWithdraw,
}: WalletPageProps) {
  const [showBalance, setShowBalance] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasConnectedAccount, setHasConnectedAccount] = useState<boolean | null>(null);
  const [availableCents, setAvailableCents] = useState(0);
  const [pendingCents, setPendingCents] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payoutSummary, setPayoutSummary] = useState<SellerPayoutSummaryResponse | null>(null);
  const [payoutAccounts, setPayoutAccounts] = useState<SellerPayoutAccountItem[]>([]);
  const [accountStatus, setAccountStatus] = useState<SellerAccountStatusResponse | null>(null);
  const [payoutFailures, setPayoutFailures] = useState<SellerPayoutFailureItem[]>([]);
  const [connectReservedCents, setConnectReservedCents] = useState(0);
  const [escrowCents, setEscrowCents] = useState(0);
  const [linkLoading, setLinkLoading] = useState(false);

  const buildReturnUrl = useCallback(() => buildStripeWalletReturnUrl("onboarding_done"), []);
  const buildRefreshUrl = useCallback(() => buildStripeWalletReturnUrl("refresh"), []);

  const loadWallet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let profile;
      try {
        profile = await getProfile();
      } catch {
        setHasConnectedAccount(false);
        setLoading(false);
        return;
      }
      const connected = !!(profile.stripe_connected_account_id?.trim());
      setHasConnectedAccount(connected);
      if (!connected) {
        setLoading(false);
        return;
      }
      const [balanceRes, txRes, summaryRes, accountRes, statusRes, failuresRes] = await Promise.all([
        getSellerBalance(),
        getSellerBalanceTransactions(50),
        getSellerPayoutSummary(),
        getSellerPayoutAccount(),
        getSellerAccountStatus().catch(() => null),
        getSellerPayoutFailures(5).catch(() => ({ data: [] as SellerPayoutFailureItem[] })),
      ]);
      setAvailableCents(balanceRes.available_cents);
      setPendingCents(balanceRes.pending_cents);
      setConnectReservedCents(balanceRes.connect_reserved_cents ?? 0);
      setEscrowCents(balanceRes.escrow_cents ?? 0);
      setTransactions((txRes.data || []).map((item) => balanceTxToTransaction(item, t("transactionDefaultTitle"))));
      setPayoutSummary(summaryRes || null);
      setPayoutAccounts(accountRes.data || []);
      setAccountStatus(statusRes ?? null);
      setPayoutFailures(failuresRes?.data ?? []);
    } catch (e) {
      const err = e as Error & { status?: number; error?: string };
      if (err.status === 400 && (err.error === "no connected account; complete payout setup first" || (err as { message?: string }).message?.includes("complete"))) {
        setHasConnectedAccount(false);
      } else {
        setError(err.message || t("errorLoadWallet"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  // Refetch when returning from Stripe (e.g. URL has onboarding=done)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("wallet") === "1" && (params.get("onboarding") === "done" || params.get("refresh") === "1")) {
      loadWallet();
      const u = new URL(window.location.href);
      u.searchParams.delete("wallet");
      u.searchParams.delete("onboarding");
      u.searchParams.delete("refresh");
      window.history.replaceState({}, "", u.toString());
    }
  }, [loadWallet]);

  const handleCompleteSetup = useCallback(async () => {
    const returnUrl = buildReturnUrl();
    const refreshUrl = buildRefreshUrl();
    if (!returnUrl || !refreshUrl) {
      setError(stripeConnectConfigErrorMessage());
      return;
    }
    setLinkLoading(true);
    let navigatingToStripe = false;
    try {
      const { url } = await postSellerConnectOnboarding({
        return_url: returnUrl,
        refresh_url: refreshUrl,
        country: "BR",
      });
      const trimmed = url?.trim();
      if (trimmed) {
        navigatingToStripe = true;
        window.location.href = trimmed;
      }
    } catch (e) {
      setError((e as Error).message || t("errorOpenSetup"));
    } finally {
      if (!navigatingToStripe) setLinkLoading(false);
    }
  }, [buildReturnUrl, buildRefreshUrl]);

  const handleUpdatePayoutDetails = useCallback(async () => {
    const returnUrl = buildReturnUrl();
    const refreshUrl = buildRefreshUrl();
    if (!returnUrl || !refreshUrl) {
      setError(stripeConnectConfigErrorMessage());
      return;
    }
    setLinkLoading(true);
    let navigatingToStripe = false;
    try {
      const { url } = await postSellerConnectUpdate({
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });
      const trimmed = url?.trim();
      if (trimmed) {
        navigatingToStripe = true;
        window.location.href = trimmed;
      }
    } catch (e) {
      setError((e as Error).message || t("errorOpenUpdate"));
    } finally {
      if (!navigatingToStripe) setLinkLoading(false);
    }
  }, [buildReturnUrl, buildRefreshUrl]);

  const balance = availableCents / 100;
  const pending = pendingCents / 100;
  // "A Receber" = same as "Pendente" (balance API is source of truth for pending receivables)
  const totalReceivables = pending;
  const totalPayables = useMemo(() => transactions.filter((t) => t.type === "paid" && t.status === "pending").reduce((s, t) => s + t.amount, 0), [transactions]);
  // Count of pending transactions (typically pending receivables in seller wallet)
  const pendingCount = useMemo(() => transactions.filter((t) => t.status === "pending").length, [transactions]);

  const handleWithdraw = useCallback(() => {
    if (onNavigateToWithdraw) {
      onNavigateToWithdraw();
    } else {
      alert(`${t("withdrawAlertTitle")}: ${formatCurrency(balance)}\n\n${t("withdrawComingSoon")}`);
    }
  }, [balance, onNavigateToWithdraw]);

  const handleAddFunds = useCallback(() => {
    alert(t("addFundsComingSoon"));
  }, []);

  const defaultBank = useMemo(() => payoutAccounts.find((a) => a.default_for_currency) || payoutAccounts[0], [payoutAccounts]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label={t("back")}
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">{t("pageTitle")}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto">
        {error && (
          <div className="mx-4 mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm">
            {error}
          </div>
        )}

        {loading && hasConnectedAccount === null && (
          <div className="mx-4 mt-4 p-6 text-center text-muted-foreground">{t("loading")}</div>
        )}

        {!loading && hasConnectedAccount === false && (
          <div className="mx-4 mt-4 p-6 bg-card rounded-xl border border-border">
            <p className="text-muted-foreground mb-4">
              {t("setupIntro")}
            </p>
            <button
              onClick={handleCompleteSetup}
              disabled={linkLoading}
              className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
              aria-busy={linkLoading}
            >
              <WalletStripeBusyLabel busy={linkLoading} idleLabel={t("setupCta")} />
            </button>
          </div>
        )}

        {!loading && hasConnectedAccount === true && (
          <>
            <BalanceCard
              balance={balance}
              pending={pending}
              reserved={connectReservedCents / 100}
              escrow={escrowCents / 100}
              showBalance={showBalance}
              onToggleBalance={() => setShowBalance(!showBalance)}
              onWithdraw={handleWithdraw}
              onAddFunds={handleAddFunds}
            />

            <div className="px-4 space-y-4">
              {accountStatus && ((accountStatus.currently_due?.length ?? 0) + (accountStatus.past_due?.length ?? 0)) > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">{t("completeVerification")}</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                    {t("verificationHint")}
                  </p>
                  <button
                    type="button"
                    onClick={handleUpdatePayoutDetails}
                    disabled={linkLoading}
                    className="text-sm font-medium text-amber-800 dark:text-amber-200 hover:underline disabled:opacity-50 inline-flex"
                    aria-busy={linkLoading}
                  >
                    <WalletStripeBusyLabel busy={linkLoading} idleLabel={t("updatePayoutDetails")} />
                  </button>
                </div>
              )}
              {accountStatus && !accountStatus.payouts_enabled && accountStatus.details_submitted && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
                  <p className="text-sm font-medium text-destructive">{t("payoutsSuspended")}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {accountStatus.disabled_reason
                      ? `${t("reason")}: ${accountStatus.disabled_reason.replace(/_/g, " ")}. `
                      : ""}
                    {t("updateToReactivate")}
                  </p>
                  <button
                    type="button"
                    onClick={handleUpdatePayoutDetails}
                    disabled={linkLoading}
                    className="text-sm font-medium text-primary hover:underline mt-2 disabled:opacity-50 inline-flex"
                    aria-busy={linkLoading}
                  >
                    <WalletStripeBusyLabel busy={linkLoading} idleLabel={t("updatePayoutDetails")} />
                  </button>
                </div>
              )}
              {accountStatus?.schedule_interval && accountStatus.schedule_interval !== "manual" && (
                <div className="bg-card rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground">
                    {accountStatus.schedule_interval === "daily" && t("scheduleDaily")}
                    {accountStatus.schedule_interval === "weekly" && t("scheduleWeekly")}
                    {accountStatus.schedule_interval === "monthly" && t("scheduleMonthly")}
                  </p>
                </div>
              )}
              {payoutFailures.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
                  <p className="text-sm font-medium text-destructive">{t("lastPayoutFailed")}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {payoutFailures[0].failure_message || payoutFailures[0].failure_code || t("payoutErrorFallback")}{" "}
                    {t("amount")}: {formatCurrency(payoutFailures[0].amount_cents / 100)}.
                  </p>
                  <button
                    type="button"
                    onClick={handleUpdatePayoutDetails}
                    disabled={linkLoading}
                    className="text-sm font-medium text-primary hover:underline mt-2 disabled:opacity-50 inline-flex"
                    aria-busy={linkLoading}
                  >
                    <WalletStripeBusyLabel busy={linkLoading} idleLabel={t("updatePayoutDetails")} />
                  </button>
                </div>
              )}
              {payoutSummary != null && (
                <div className="bg-card rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground mb-1">{t("nextPayout")}</p>
                  <p className="font-medium">
                    {formatCurrency(payoutSummary.amount_cents / 100)} {t("nextPayoutDatePreposition")}{" "}
                    {formatDate(new Date(payoutSummary.arrival_date * 1000))}
                  </p>
                </div>
              )}
              {defaultBank && (
                <div className="bg-card rounded-xl p-4 border border-border">
                  <p className="text-xs text-muted-foreground mb-1">{t("payoutAccount")}</p>
                  <p className="font-medium">
                    •••• {defaultBank.last4} {defaultBank.bank_name ? `— ${defaultBank.bank_name}` : ""}
                  </p>
                  <p className="text-sm text-muted-foreground capitalize">{defaultBank.status}</p>
                  <button
                    type="button"
                    onClick={handleUpdatePayoutDetails}
                    disabled={linkLoading}
                    className="mt-3 text-sm text-primary font-medium hover:underline disabled:opacity-50 inline-flex"
                    aria-busy={linkLoading}
                  >
                    <WalletStripeBusyLabel busy={linkLoading} idleLabel={t("updatePayoutDetails")} />
                  </button>
                </div>
              )}
              {hasConnectedAccount === true && !defaultBank && (
                <div className="bg-card rounded-xl p-4 border border-border">
                  <p className="text-muted-foreground text-sm mb-2">{t("noBankAccount")}</p>
                  <button
                    type="button"
                    onClick={handleUpdatePayoutDetails}
                    disabled={linkLoading}
                    className="text-primary font-medium hover:underline disabled:opacity-50 inline-flex"
                    aria-busy={linkLoading}
                  >
                    <WalletStripeBusyLabel busy={linkLoading} idleLabel={t("updatePayoutDetails")} />
                  </button>
                </div>
              )}
            </div>

            <QuickStats
              totalReceivables={totalReceivables}
              totalPayables={totalPayables}
              escrowCents={escrowCents}
              pendingCount={pendingCount}
              showBalance={showBalance}
            />

            <div className="px-4">
              <h2 className="font-medium mb-4">{t("transactions")}</h2>
              <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden mb-4">
                {transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <TransactionItem key={tx.id} transaction={tx} showBalance={showBalance} />
                  ))
                ) : (
                  <div className="p-8 text-center text-muted-foreground">{t("noTransactions")}</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}
